/**
 * SARKY wishlist bottom sheet -- opens on a not-yet-saved
 * [data-sarky-wishlist-heart] click (see the intercept added to
 * assets/sarky-wishlist.js).
 *
 * REDESIGN WISHLIST VARIANT SELECTION (this pass): replaced the old flat
 * "Dark Brown / XS" combined-variant-row list with separate COLOR
 * (swatches) and SIZE (plain buttons) sections, matching the same shared
 * SARKY swatch system product cards already use -- no second swatch
 * implementation:
 *
 * - Real product/variant/price/availability data still comes from
 *   Shopify's own tokenless `/products/{handle}.js` endpoint (unchanged
 *   from before -- the same technique assets/sarky-wishlist-page.js
 *   already uses, no new backend). `product.options[]` and
 *   `variants[].option1/2/3` are Shopify's own, already-resolved variant
 *   data -- this file never invents its own variant/availability logic,
 *   it only cross-references what Shopify already computed.
 * - Real Color SWATCH VISUALS (the actual resolved dot/photo per color
 *   name -- native swatch.image -> native swatch.color -> the shared
 *   safe color-name map -> photo fallback) come from a second, small
 *   fetch against `/products/{handle}?view=sarky-wishlist-swatches`
 *   (templates/product.sarky-wishlist-swatches.liquid), which calls
 *   snippets/swatch.liquid directly -- the exact same snippet
 *   snippets/variant-swatches.liquid (product cards, PDP) renders. The
 *   two fetches are correlated purely by color NAME (a plain string,
 *   e.g. "Dark Brown"), never by assuming option position.
 *
 * SMART FLOW: a product with no meaningful choice (0 or 1 variant, or a
 * Color/Size option that only ever has one real value) saves instantly
 * with NO sheet ever shown -- the fetch above happens BEFORE the dialog
 * opens specifically so this can be decided first; previously the
 * dialog opened synchronously and only the size picker inside it was
 * conditional.
 *
 * PRESERVE SAVED WISHLIST VARIANT / SIZE (unchanged from before):
 * window.SarkyWishlist.add(handle, selectedVariantId) -- schema v2
 * (assets/sarky-wishlist.js) -- stores the exact variant resolved here,
 * so the Wishlist page later renders/links that same variant instead of
 * the product's default. A single-variant product (no sheet shown at
 * all) still passes the real, single resolvable variant id rather than
 * null, since it's already known with certainty.
 */
(function () {
  'use strict';

  var dialog = null;
  var contentEl = null;
  var currentHandle = null;
  var currentAbort = null;

  // Per-open selection state.
  var currentProduct = null;
  var colorIndex = -1;
  var otherIndex = -1;
  var otherName = '';
  var selectedColor = null;
  var selectedSize = null;
  var defaultImage = null;

  function qs(root, selector) {
    return root.querySelector('[data-role="' + selector + '"]');
  }

  function formatMoney(cents, currency, locale) {
    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  }

  function setState(state) {
    if (!contentEl) return;
    contentEl.setAttribute('data-state', state);

    var loading = qs(contentEl, 'loading');
    var error = qs(contentEl, 'error');
    var product = qs(contentEl, 'product');

    if (loading) loading.hidden = state !== 'loading';
    if (error) error.hidden = state !== 'error';
    if (product) product.hidden = state !== 'product';
  }

  /**
   * Finds Shopify's own "Color" option (by name, matching the exact same
   * convention snippets/variant-swatches.liquid / sarky-product-has-
   * swatches.liquid already use) and the next option after it (if any)
   * to treat as the Size section - generically by position, not by
   * assuming the literal name "Size", so any single second option still
   * gets its own correctly-labelled section.
   */
  function resolveOptionRoles(product) {
    var options = product.options || [];
    var cIndex = -1;
    var oIndex = -1;
    var oName = '';

    for (var i = 0; i < options.length; i++) {
      if (options[i].name === 'Color') {
        cIndex = i;
        break;
      }
    }

    for (var j = 0; j < options.length; j++) {
      if (j !== cIndex) {
        oIndex = j;
        oName = options[j].name;
        break;
      }
    }

    return { colorIndex: cIndex, otherIndex: oIndex, otherName: oName };
  }

  function optionKey(index) {
    return 'option' + (index + 1);
  }

  function variantsForColor(variants, colorName) {
    if (colorIndex === -1) return variants;
    var key = optionKey(colorIndex);
    return variants.filter(function (v) {
      return v[key] === colorName;
    });
  }

  function findVariant(variants, colorName, sizeName) {
    var key1 = colorIndex !== -1 ? optionKey(colorIndex) : null;
    var key2 = otherIndex !== -1 ? optionKey(otherIndex) : null;

    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (key1 && v[key1] !== colorName) continue;
      if (key2 && v[key2] !== sizeName) continue;
      return v;
    }

    return null;
  }

  function renderError(message) {
    var messageEl = qs(contentEl, 'error-message');
    if (messageEl) messageEl.textContent = message;
    setState('error');
  }

  // Builds the swatch <button> row from the real, server-rendered
  // fragment (see the file doc comment) - never re-derives a color's
  // visual from raw JSON.
  function renderSwatches(swatchesDoc, colorValues) {
    var swatchesWrap = qs(contentEl, 'swatches');
    if (!swatchesWrap) return;
    swatchesWrap.innerHTML = '';

    if (!swatchesDoc) return;

    var byName = {};
    var buttons = swatchesDoc.querySelectorAll('[data-color-name]');
    for (var i = 0; i < buttons.length; i++) {
      byName[buttons[i].getAttribute('data-color-name')] = buttons[i];
    }

    colorValues.forEach(function (colorName) {
      var source = byName[colorName];
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'sarky-wishlist-sheet__swatch';
      button.setAttribute('data-color-name', colorName);
      button.setAttribute('aria-label', colorName);
      button.setAttribute('aria-pressed', 'false');

      if (source) {
        button.innerHTML = source.innerHTML;
        var image = source.getAttribute('data-color-image');
        if (image) button.setAttribute('data-color-image', image);
      }

      var hasAnyAvailable = variantsForColor(currentProduct.variants, colorName).some(function (v) {
        return v.available;
      });
      button.disabled = !hasAnyAvailable;

      button.addEventListener('click', function () {
        selectColor(colorName);
      });

      swatchesWrap.appendChild(button);
    });
  }

  function selectColor(colorName) {
    selectedColor = colorName;

    var swatchesWrap = qs(contentEl, 'swatches');
    var nameEl = qs(contentEl, 'selected-color-name');
    var selectedButton = null;

    if (swatchesWrap) {
      var buttons = swatchesWrap.querySelectorAll('.sarky-wishlist-sheet__swatch');
      for (var i = 0; i < buttons.length; i++) {
        var isMatch = buttons[i].getAttribute('data-color-name') === colorName;
        buttons[i].setAttribute('aria-pressed', isMatch ? 'true' : 'false');
        if (isMatch) selectedButton = buttons[i];
      }
    }

    if (nameEl) nameEl.textContent = colorName || '';

    // Update the thumbnail to this color's own photo where the product's
    // real media actually supports it - otherwise the product's default
    // image (set once in renderProduct) is left untouched.
    var image = qs(contentEl, 'image');
    if (image && selectedButton) {
      var colorImage = selectedButton.getAttribute('data-color-image');
      if (colorImage) image.src = colorImage;
      else if (defaultImage) image.src = defaultImage;
    }

    renderSizesForColor(colorName);
    updateSaveDisabled();
  }

  function renderSizesForColor(colorName) {
    if (otherIndex === -1) return;

    var sizesList = qs(contentEl, 'sizes-list');
    if (!sizesList) return;

    var sizeValues = (currentProduct.options[otherIndex] || {}).values || [];
    var availableForColor = variantsForColor(currentProduct.variants, colorName);

    var wasSelectedStillValid = false;

    sizesList.innerHTML = '';

    sizeValues.forEach(function (sizeName) {
      var variant = findVariant(availableForColor.length ? availableForColor : currentProduct.variants, colorName, sizeName);

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'sarky-wishlist-sheet__size';
      button.textContent = sizeName;
      button.setAttribute('aria-pressed', 'false');
      button.disabled = !variant || !variant.available;

      if (selectedSize === sizeName && !button.disabled) {
        button.setAttribute('aria-pressed', 'true');
        wasSelectedStillValid = true;
      }

      button.addEventListener('click', function () {
        selectSize(sizeName);
      });

      sizesList.appendChild(button);
    });

    if (!wasSelectedStillValid) {
      selectedSize = null;
    }
  }

  function selectSize(sizeName) {
    selectedSize = sizeName;

    var sizesList = qs(contentEl, 'sizes-list');
    if (sizesList) {
      var buttons = sizesList.querySelectorAll('.sarky-wishlist-sheet__size');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].setAttribute('aria-pressed', buttons[i].textContent === sizeName ? 'true' : 'false');
      }
    }

    updateSaveDisabled();
  }

  function resolvedVariantId() {
    if (colorIndex === -1 && otherIndex === -1) {
      var only = currentProduct.variants[0];
      return only ? only.id : null;
    }

    if (colorIndex !== -1 && !selectedColor) return null;
    if (otherIndex !== -1 && !selectedSize) return null;

    var variant = findVariant(currentProduct.variants, selectedColor, selectedSize);
    return variant ? variant.id : null;
  }

  function updateSaveDisabled() {
    var saveButton = qs(contentEl, 'save');
    if (!saveButton) return;
    saveButton.disabled = !resolvedVariantId();
  }

  function renderProduct(product, swatchesDoc) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    var locale = document.documentElement.lang || 'en';

    currentProduct = product;
    selectedColor = null;
    selectedSize = null;

    var roles = resolveOptionRoles(product);
    colorIndex = roles.colorIndex;
    otherIndex = roles.otherIndex;
    otherName = roles.otherName;

    var image = qs(contentEl, 'image');
    var title = qs(contentEl, 'title');
    var price = qs(contentEl, 'price');
    var colorSection = qs(contentEl, 'color-section');
    var sizeSection = qs(contentEl, 'size-section');
    var sizeLabel = qs(contentEl, 'size-label');
    var soldOutNote = qs(contentEl, 'sold-out-note');
    var saveButton = qs(contentEl, 'save');

    defaultImage = product.featured_image || (product.images && product.images[0]) || null;

    if (image) {
      if (defaultImage) {
        image.src = defaultImage;
        image.alt = product.title || '';
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    if (title) title.textContent = product.title || '';
    if (price) price.textContent = formatMoney(product.price, currency, locale);

    var colorValues = colorIndex !== -1
      ? (product.options[colorIndex].values || [])
      : [];
    var showColor = colorIndex !== -1 && colorValues.length > 1;

    var sizeValues = otherIndex !== -1
      ? (product.options[otherIndex].values || [])
      : [];
    var showSize = otherIndex !== -1 && sizeValues.length > 1;

    if (colorSection) colorSection.hidden = !showColor;
    if (sizeSection) sizeSection.hidden = !showSize;
    if (sizeLabel) sizeLabel.textContent = otherName || 'Size';

    if (showColor) {
      renderSwatches(swatchesDoc, colorValues);
    }

    // Default selection mirrors Shopify's own resolution (the same safe
    // "let Shopify decide" fallback used everywhere else in this theme) -
    // the classic `/products/{handle}.js` endpoint has no
    // `selected_or_first_available_variant` field of its own (that's a
    // Liquid-only convenience), so it's derived here the exact same way
    // Shopify's own Liquid object would: first available variant, or the
    // first variant at all if none are - so the sheet never opens with a
    // blank/ambiguous state.
    //
    // Deliberately keyed on colorIndex/otherIndex (does this OPTION
    // exist at all), never on showColor/showSize (is its SECTION visibly
    // shown) - an option that exists but only ever has one real value
    // (section hidden, per SMART FLOW) still needs its single value
    // recorded in state, or resolvedVariantId() would wait forever for a
    // selection the UI never gives the visitor a chance to make.
    var initialVariant = pickDefaultVariant(product);

    if (colorIndex !== -1 && initialVariant) {
      selectColor(initialVariant[optionKey(colorIndex)]);
    } else if (otherIndex !== -1) {
      renderSizesForColor(null);
    }

    if (otherIndex !== -1 && initialVariant && initialVariant.available) {
      selectSize(initialVariant[optionKey(otherIndex)]);
    }

    if (soldOutNote) soldOutNote.hidden = product.available !== false;

    if (saveButton) {
      saveButton.setAttribute('data-state', 'idle');
      var label = qs(contentEl, 'save-label');
      if (label) label.textContent = window.__sarkyWishlistSheetStrings.add;
    }

    updateSaveDisabled();
  }

  function parseSwatchesHtml(html) {
    if (!html) return null;
    try {
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (error) {
      return null;
    }
  }

  function fetchProduct(handle, signal) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
      signal: signal,
    }).then(function (response) {
      if (!response.ok) throw new Error('not-found');
      return response.json();
    });
  }

  function fetchSwatches(handle, signal) {
    return fetch('/products/' + encodeURIComponent(handle) + '?view=sarky-wishlist-swatches', {
      signal: signal,
    })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .catch(function () {
        return '';
      });
  }

  // A product with no real choice to make (0/1 variant, or every option
  // it has only ever resolves to a single value) is saved immediately,
  // with the sheet never opened at all - the SMART FLOW requirement.
  function needsSheet(product, roles) {
    var colorValues = roles.colorIndex !== -1
      ? (product.options[roles.colorIndex].values || [])
      : [];
    var otherValues = roles.otherIndex !== -1
      ? (product.options[roles.otherIndex].values || [])
      : [];

    return colorValues.length > 1 || otherValues.length > 1;
  }

  function pickDefaultVariant(product) {
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }
    return variants[0] || null;
  }

  function saveInstantly(handle, product, button) {
    if (!window.SarkyWishlist) return;
    var variant = pickDefaultVariant(product);
    window.SarkyWishlist.add(handle, variant ? variant.id : null);
    window.SarkyWishlist.syncAllHearts();
    if (button) window.SarkyWishlist.playPop(button);
  }

  // MICRO POLISH - root cause of the unwanted ring around the close
  // button: native <dialog>.showModal() auto-focuses the first
  // focusable descendant when nothing has a real effect via `autofocus`
  // (confirmed live - the close button ends up as document.activeElement
  // the instant the sheet opens, and :focus-visible correctly matches
  // that programmatic focus, drawing its ring around the round close
  // button). This affects every dialog in the theme built the same way
  // (search modal, quick-add) since they all put a close button first -
  // out of scope to fix broadly here.
  //
  // Scoped fix: move focus to the <dialog> itself (tabindex="-1" in the
  // markup makes it a valid focus target; `outline: none` on it in
  // snippets/sarky-wishlist-sheet.liquid means this focus sink never
  // shows its own ring either) right after the dialog actually opens.
  // Listening for assets/dialog.js's own 'dialog:open' event (fired
  // synchronously right after showModal() inside a requestAnimationFrame
  // callback) is required here, not just calling this straight after
  // showDialog() - showDialog() only *schedules* that same rAF callback,
  // so a synchronous call would run before showModal() (and the
  // browser's own auto-focus) ever happens, doing nothing. This listens
  // on the shared <dialog-component> element itself - a normal, public
  // DOM event - not a change to assets/dialog.js.
  function focusDialogSurface() {
    var surface = dialog && dialog.refs && dialog.refs.dialog;
    if (surface && typeof surface.focus === 'function') {
      surface.focus({ preventScroll: true });
    }
  }

  function open(handle, button) {
    if (!dialog || typeof dialog.showDialog !== 'function') return false;

    currentHandle = handle;
    currentAbort && currentAbort.abort();
    currentAbort = new AbortController();
    var signal = currentAbort.signal;

    button && button.classList.add('is-loading');

    Promise.all([fetchProduct(handle, signal), fetchSwatches(handle, signal)])
      .then(function (results) {
        if (currentHandle !== handle) return;

        button && button.classList.remove('is-loading');

        var product = results[0];
        var swatchesDoc = parseSwatchesHtml(results[1]);
        var roles = resolveOptionRoles(product);

        if (!needsSheet(product, roles)) {
          saveInstantly(handle, product, button);
          return;
        }

        setState('loading');
        dialog.showDialog();
        renderProduct(product, swatchesDoc);
        setState('product');
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        button && button.classList.remove('is-loading');
        dialog.showDialog();
        renderError(window.__sarkyWishlistSheetStrings.error);
      });

    var saveButton = contentEl && qs(contentEl, 'save');
    if (saveButton) {
      saveButton.onclick = function () {
        if (!window.SarkyWishlist) return;
        var variantId = resolvedVariantId();
        if (!variantId) return;

        saveButton.disabled = true;
        window.SarkyWishlist.add(handle, variantId);
        saveButton.setAttribute('data-state', 'saved');
        var label = qs(contentEl, 'save-label');
        if (label) label.textContent = window.__sarkyWishlistSheetStrings.added;

        if (button) {
          window.SarkyWishlist.syncAllHearts();
          window.SarkyWishlist.playPop(button);
        }

        // MICRO POLISH: was 550ms - the sheet visibly closed before the
        // saved/success state (solid green, "Saved to Wishlist", check
        // icon) had time to actually register with the visitor. 1600ms
        // gives it a real, readable confirmation beat without stalling
        // the flow.
        window.setTimeout(function () {
          dialog.closeDialog();
        }, 2600);
      };
    }

    var retryButton = contentEl && qs(contentEl, 'error-retry');
    if (retryButton) {
      retryButton.onclick = function () {
        open(handle, button);
      };
    }

    return true;
  }

  function init() {
    var dialogEl = document.getElementById('sarky-wishlist-dialog');
    contentEl = document.getElementById('sarky-wishlist-sheet-content');

    if (!dialogEl || !contentEl) return;

    dialog = dialogEl;

    // MICRO POLISH: assets/dialog.js dispatches 'dialog:open' on this
    // element synchronously right after its real showModal() call (both
    // deferred together inside its own requestAnimationFrame callback) -
    // this is the only point in time where overriding the browser's own
    // close-button auto-focus actually works. Bound once here, not per
    // open() call, since open() can run repeatedly (once per heart tap).
    dialog.addEventListener('dialog:open', focusDialogSurface);

    window.__sarkyWishlistSheetStrings = {
      add: (qs(contentEl, 'save-label') || {}).textContent || 'Save to Wishlist',
      added: 'Saved to Wishlist',
      error: 'This product could not be loaded.',
    };

    window.SarkyWishlistSheet = { open: open };
    window.dispatchEvent(new CustomEvent('sarky:wishlist-sheet:ready'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
